import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { getSupabaseFunctionErrorMessage } from "@/utils/supabaseFunctionUtils"

import type {
  AudienceOptions,
  IncentiveFields,
  IncentiveListRow,
  IncentiveStatus,
  RaiseResult,
  ReconciledIncentive,
  StatusChangeTarget,
} from "../../shared/incentives"

export {
  APP_COMMS_MAX_LENGTHS,
  INCENTIVE_STATUSES,
  ROUTE_CYCLES,
  ROUTE_TYPES,
} from "../../shared/incentives"
export type {
  AppComms,
  Audience,
  AudienceMiss,
  AudienceOptions,
  DriverTicket,
  IncentiveFields,
  IncentiveListRow,
  IncentiveStatus,
  IneligibleReason,
  RaiseResult,
  ReconciledDriver,
  ReconciledIncentive,
  RouteCycle,
  RouteType,
  TicketStatus,
} from "../../shared/incentives"

export type Incentive = IncentiveListRow & IncentiveFields

export type IncentiveFilters = {
  depot?: string
  driverType?: string
  statuses: IncentiveStatus[]
}

const fetchIncentives = async (filters: IncentiveFilters): Promise<IncentiveListRow[]> => {
  const params = new URLSearchParams()
  if (filters.depot) params.set("depot", filters.depot)
  if (filters.driverType) params.set("driver_type", filters.driverType)
  if (filters.statuses.length > 0) params.set("status", filters.statuses.join(","))

  const query = params.toString()
  const { data, error } = await supabase.functions.invoke(
    query ? `incentives?${query}` : "incentives",
    { method: "GET" },
  )
  if (error) throw new Error(await getSupabaseFunctionErrorMessage(error))
  return data.data
}

export const useIncentives = (filters: IncentiveFilters) =>
  useQuery({
    queryKey: ["incentives", filters],
    queryFn: () => fetchIncentives(filters),
  })

const callIncentives = async <T>(
  path: string,
  method: "GET" | "POST" | "PUT",
  body?: unknown,
): Promise<T> => {
  const { data, error } = await supabase.functions.invoke(path, { method, body })
  if (error) throw new Error(await getSupabaseFunctionErrorMessage(error))
  return data.data
}

export const useIncentive = (id: string | undefined) =>
  useQuery({
    queryKey: ["incentive", id],
    queryFn: () => callIncentives<Incentive>(`incentives/${id}`, "GET"),
    enabled: Boolean(id),
  })

// Every call reads Moneta, so a reconcile isn't retried or refetched on focus.
export const useIncentiveReconcile = (week: string) =>
  useQuery({
    queryKey: ["incentive-reconcile", week],
    queryFn: () =>
      callIncentives<ReconciledIncentive[]>(`incentives/reconcile?week=${week}`, "GET"),
    retry: false,
    refetchOnWindowFocus: false,
  })

export const useIncentiveAudienceOptions = () =>
  useQuery({
    queryKey: ["incentive-audience-options"],
    queryFn: () => callIncentives<AudienceOptions>("incentives/audience-options", "GET"),
    staleTime: 60 * 60 * 1000,
  })

const useIncentiveMutation = <T>(mutationFn: (variables: T) => Promise<Incentive>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: (incentive) => {
      queryClient.setQueryData(["incentive", incentive.id], incentive)
      queryClient.invalidateQueries({ queryKey: ["incentives"] })
    },
  })
}

// A live incentive takes only its end date, so the caller sends just the fields it may change.
export const useSaveIncentive = () =>
  useIncentiveMutation(({ id, body }: { id?: string; body: Partial<IncentiveFields> }) =>
    id
      ? callIncentives<Incentive>(`incentives/${id}`, "PUT", body)
      : callIncentives<Incentive>("incentives", "POST", body),
  )

export const useChangeIncentiveStatus = () =>
  useIncentiveMutation(({ id, status }: { id: string; status: StatusChangeTarget }) =>
    callIncentives<Incentive>(`incentives/${id}/status`, "POST", { status }),
  )

// A ticket raised for one week can show as paid in a later one, so every week is refetched.
// Returning the refetch keeps the mutation pending until the screen shows what was raised.
const useRaiseMutation = <T, R>(mutationFn: (variables: T) => Promise<R>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["incentive-reconcile"] }),
        queryClient.invalidateQueries({ queryKey: ["incentives"] }),
      ]),
  })
}

export const useRaiseMissingTickets = () =>
  useRaiseMutation(({ incentiveId, week }: { incentiveId: string; week: string }) =>
    callIncentives<RaiseResult>(`incentives/${incentiveId}/raise`, "POST", { week }),
  )

export const useReraiseTicket = () =>
  useRaiseMutation(({ incentiveId, ticketId }: { incentiveId: string; ticketId: string }) =>
    callIncentives<{ ticket_id: string; ticket_status: "Pending" }>(
      `incentives/${incentiveId}/tickets/${ticketId}/reraise`,
      "POST",
    ),
  )
